import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from '@reduxjs/toolkit'
import type { TrackId, WriterSplit } from 'types/track'
import { createAppSlice } from '@disco/utils/slices'
import {
  saveWriterSplits as saveWriterSplitsApi,
  batchSaveWriterSplits as batchSaveWriterSplitsApi,
} from '@disco/api/rights-data'
import type { WriterSplitResponseItem } from '@disco/api/rights-data'
import {
  isBatchWriterSplitsSuccessResponse,
  isBatchWriterSplitsErrorResponse,
} from '@disco/api/rights-data'
import { pushUINotification } from '@disco/utils/notifications'
import { parseServerError } from '@disco/utils/captureError'
import cuid from 'cuid'
import {
  parseSplitValidationErrors,
  hasSplitValidationErrors,
  hasTrackLevelValidationErrors,
} from '@disco/utils/splitValidation'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import type {
  RightsSplitChain,
  RightsType,
  SplitValidationError,
  TrackLevelValidationError,
} from 'types/rights-data'
import type { State } from './index'

/**
 * Special key used for bulk editing mode when multiple tracks are selected.
 * This allows the UI to manage splits in a staging area before applying to multiple tracks.
 */
export const BULK_EDIT_KEY = 'bulk' as const
export type BulkEditKey = typeof BULK_EDIT_KEY

/**
 * Key type for splits storage - either a real track ID or the bulk edit key.
 */
export type SplitsKey = TrackId | BulkEditKey

/**
 * Represents a split in the rights management UI.
 * Can reference existing entities by number ID or new entities by temporary string ID.
 */
export type SplitData = PublishingSplitData | RecordingSplitData

export type RecordingSplitData = {
  kind: RightsType
  id: number | string
  rightsholderId: number | null
  labelId: number | null
  split: number | null
  isNew?: boolean
}

export type PublishingSplitData = {
  /** number for existing splits, string (temp ID) for new splits */
  id: number | string
  kind: RightsType
  /** Writer entity ID, or null if no writer selected */
  writerId: number | null
  publisherId?: number | null
  /** Publishing Admin ID */
  adminId?: number | null
  proId?: number | null
  /** Percentage value */
  split: number | null
  /** Flag for UI to know if this is pending creation */
  isNew?: boolean
}

export const hasSplitFieldValue = (
  value: number | null | undefined,
): value is number => typeof value === 'number' && value > 0

/**
 * Rights data state for a single track.
 */
export type TrackRightsState = {
  /** Array of splits for this track */
  splits: SplitData[]
  /** Has unsaved changes */
  isDirty: boolean
  isLoading: boolean
  error: string | null
  /** Field-level validation errors from the server, indexed by split position */
  validationErrors: SplitValidationError[]
  /** Track-level validation errors (e.g., duplicate writer-publisher combinations) */
  trackLevelErrors?: TrackLevelValidationError[]
}

/**
 * Result for a single track in bulk save operation.
 */
export type BulkSaveTrackResult = {
  trackId: TrackId
  success: boolean
  /** Single error message (for backward compatibility) or multiple errors */
  error?: string
  /** Array of error messages when track has multiple validation errors */
  errors?: string[]
}

/**
 * State for tracking bulk save operation progress.
 */
export type BulkSaveState = {
  isLoading: boolean
  /** Results per track after operation completes */
  results: BulkSaveTrackResult[]
  /** Number of successfully updated tracks */
  successCount: number
  /** Number of failed tracks */
  failureCount: number
  /** General error message if the entire operation failed */
  error: string | null
}

const initialBulkSaveState: BulkSaveState = {
  isLoading: false,
  results: [],
  successCount: 0,
  failureCount: 0,
  error: null,
}

/**
 * Root state for rights management.
 */
export type RightsManagementState = {
  /** Splits data grouped by track ID or bulk edit key */
  splitsByTrack: Partial<Record<SplitsKey, TrackRightsState>>
  /** State for bulk save operations */
  bulkSave: BulkSaveState
}

const initialTrackRightsState: TrackRightsState = {
  splits: [],
  isDirty: false,
  isLoading: false,
  error: null,
  validationErrors: [],
}

const initialState: RightsManagementState = {
  splitsByTrack: {},
  bulkSave: initialBulkSaveState,
}

/**
 * Converts a WriterSplit from server format to SplitData format.
 */
const convertWriterSplitToSplitData = (
  writerSplit: WriterSplit,
): SplitData => ({
  id: writerSplit.id,
  kind: 'publishing',
  writerId: writerSplit.writer,
  publisherId: writerSplit.publisher,
  proId: writerSplit.pro,
  split: writerSplit.split ? parseFloat(writerSplit.split) : 0,
  isNew: false,
})

const convertRightsSplitChainToSplitData = (
  chain: RightsSplitChain,
): SplitData => {
  const writer = chain.members.find(m => m.entity.entity_type === 'Writer')
  const publisher = chain.members.find(
    m => m.entity.entity_type === 'Publisher',
  )
  const rightsholder = chain.members.find(m => m.role_type === 'RightsHolder')
  const label =
    chain.members.find(m => m.role_type === 'Label') ??
    chain.members.find(m => m.role_type === 'RightsController')
  const admin = chain.members.find(m => m.role_type === 'Administrator')
  const pro = chain.members.find(m => m.role_type === 'PRO')

  if (chain.rights_type === 'recording') {
    return {
      id: chain.id,
      kind: chain.rights_type,
      rightsholderId: rightsholder?.entity.id ?? null,
      labelId: label?.entity.id ?? null,
      split:
        chain.rights_percentage === null ? null : chain.rights_percentage / 100,
      isNew: false,
    }
  } else {
    return {
      id: chain.id,
      kind: chain.rights_type,
      writerId: writer?.entity.id ?? null,
      publisherId: publisher?.entity.id ?? null,
      adminId: admin?.entity.id ?? null,
      proId: pro?.entity.id ?? null,
      split:
        chain.rights_percentage === null ? null : chain.rights_percentage / 100,
      isNew: false,
    }
  }
}

/**
 * Creates an empty split placeholder for new writer entry.
 */
const createEmptySplit = (kind: RightsType): SplitData => {
  if (kind === 'recording') {
    return {
      id: cuid(),
      kind,
      rightsholderId: null,
      labelId: null,
      split: null,
      isNew: true,
    }
  } else {
    return {
      id: cuid(),
      kind,
      writerId: null,
      publisherId: null,
      adminId: null,
      proId: null,
      split: null,
      isNew: true,
    }
  }
}

/**
 * Creates initial track rights state with an empty split placeholder.
 */
const createInitialTrackRightsStateWithEmptySplit = (): TrackRightsState => ({
  ...initialTrackRightsState,
  splits: [createEmptySplit('publishing'), createEmptySplit('recording')],
})

/**
 * Converts an API response split item to SplitData format.
 */
const convertResponseItemToSplitData = (
  item: WriterSplitResponseItem,
): SplitData => ({
  id: item.id,
  kind: 'publishing',
  writerId: item.writer_id,
  publisherId: item.publisher_id,
  proId: item.pro_id,
  split: item.split_percentage / 100, // API uses basis points (2500 = 25%), we use percentage
  isNew: false,
})

/**
 * Groups error messages by track ID from API error responses.
 * Handles both string and number track_id values, skipping invalid entries.
 */
const groupErrorsByTrackId = (
  splits: Array<{ track_id: string | number; message: string }>,
): Map<number, string[]> => {
  const errorsByTrackId = new Map<number, string[]>()

  splits.forEach(item => {
    const trackId =
      typeof item.track_id === 'string'
        ? parseInt(item.track_id, 10)
        : (item.track_id as number)

    // Skip entries with invalid track_id to prevent NaN from corrupting the Map
    if (isNaN(trackId)) {
      console.warn('Invalid track_id in error response:', item.track_id)
      return
    }

    if (!errorsByTrackId.has(trackId)) {
      errorsByTrackId.set(trackId, [])
    }
    errorsByTrackId.get(trackId)!.push(item.message)
  })

  return errorsByTrackId
}

const updatePublishingSplit = (
  updatedSplit: PublishingSplitData,
  trackState,
  splitId: string | number,
) => {
  const hasValidWriter = hasSplitFieldValue(updatedSplit.writerId)
  const hasPublisher = hasSplitFieldValue(updatedSplit.publisherId)
  const hasAdmin = hasSplitFieldValue(updatedSplit.adminId)
  const hasPro = hasSplitFieldValue(updatedSplit.proId)
  const isEmptyPlaceholder =
    !hasValidWriter &&
    !hasPublisher &&
    !hasAdmin &&
    !hasPro &&
    updatedSplit.split == null

  if (trackState.validationErrors.length > 0) {
    if (isEmptyPlaceholder) {
      // Clear ALL validation errors for this split when it returns to empty placeholder state
      trackState.validationErrors = trackState.validationErrors.filter(
        error => error.splitId !== splitId,
      )
    } else {
      // Clear server-side validation errors for the edited split, but keep client-side errors
      // Client-side errors (e.g., from blur validation) should persist until the user fixes them
      trackState.validationErrors = trackState.validationErrors
        .map(error => {
          if (error.splitId !== splitId) return error
          // Keep only client-side field errors
          const clientSideErrors = error.fieldErrors.filter(
            fe => fe.isClientSide,
          )
          if (clientSideErrors.length === 0) return null
          return { ...error, fieldErrors: clientSideErrors }
        })
        .filter((error): error is SplitValidationError => error !== null)
    }
  }
}

const updateRecordingSplit = (
  split: RecordingSplitData,
  trackState,
  splitId: string | number,
) => {
  const hasValidLabel = hasSplitFieldValue(split.labelId)
  const hasValidRightsholder = hasSplitFieldValue(split.rightsholderId)
  const isEmptyPlaceholder =
    !hasValidLabel && !hasValidRightsholder && split.split == null

  if (trackState.validationErrors.length > 0) {
    if (isEmptyPlaceholder) {
      // Clear ALL validation errors for this split when it returns to empty placeholder state
      trackState.validationErrors = trackState.validationErrors.filter(
        error => error.splitId !== splitId,
      )
    } else {
      // Clear server-side validation errors for the edited split, but keep client-side errors
      // Client-side errors (e.g., from blur validation) should persist until the user fixes them
      trackState.validationErrors = trackState.validationErrors
        .map(error => {
          if (error.splitId !== splitId) return error
          // Keep only client-side field errors
          const clientSideErrors = error.fieldErrors.filter(
            fe => fe.isClientSide,
          )
          if (clientSideErrors.length === 0) return null
          return { ...error, fieldErrors: clientSideErrors }
        })
        .filter((error): error is SplitValidationError => error !== null)
    }
  }
}

const rightsManagementSlice = createAppSlice({
  name: 'rightsManagement',
  initialState,
  reducers: create => ({
    /**
     * Adds a new split to a track or bulk edit staging area.
     * @param trackId - The track ID or BULK_EDIT_KEY to add the split to
     * @param splitData - The split data to add
     */
    addSplit: create.reducer(
      (
        state,
        action: PayloadAction<{ trackId: SplitsKey; splitData: SplitData }>,
      ) => {
        const { trackId, splitData } = action.payload
        if (!state.splitsByTrack[trackId]) {
          state.splitsByTrack[trackId] = { ...initialTrackRightsState }
        }
        state.splitsByTrack[trackId].splits.push({
          ...splitData,
          isNew: true,
        })
        state.splitsByTrack[trackId].isDirty = false
      },
    ),

    /**
     * Updates an existing split for a track or bulk edit staging area.
     * Also clears any validation errors for the edited split.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param splitId - The split ID to update
     * @param changes - Partial split data to apply
     */
    updateSplit: create.reducer(
      (
        state,
        action: PayloadAction<{
          trackId: SplitsKey
          splitId: number | string
          changes:
            | Partial<Omit<PublishingSplitData, 'id'>>
            | Partial<Omit<RecordingSplitData, 'id'>>
        }>,
      ) => {
        const { trackId, splitId, changes } = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (!trackState) return

        const splitIndex = trackState.splits.findIndex(s => s.id === splitId)
        if (splitIndex === -1) return

        // splitIndex was found by findIndex, so trackState.splits[splitIndex] is defined
        const existingSplit = trackState.splits[splitIndex]!
        trackState.splits[splitIndex] = {
          ...existingSplit,
          ...changes,
        }
        trackState.isDirty = true

        // Check if split has been cleared to empty placeholder state
        // A split is empty when no fields have values: no writer, no publisher, no admin, no PRO, and split is 0
        // This must match the component's isEmptyPlaceholder check to avoid clearing validation errors
        // when the user fills publisher/PRO but hasn't added a writer yet
        const updatedSplit = trackState.splits[splitIndex]!

        if (updatedSplit.kind === 'recording') {
          updateRecordingSplit(
            updatedSplit as RecordingSplitData,
            trackState,
            splitId,
          )
        } else {
          updatePublishingSplit(
            updatedSplit as PublishingSplitData,
            trackState,
            splitId,
          )
        }
      },
    ),

    /**
     * Removes a split from a track or bulk edit staging area.
     * Also removes any validation errors for the deleted split.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param splitId - The split ID to remove
     */
    deletePublishingSplit: create.reducer(
      (
        state,
        action: PayloadAction<{ trackId: SplitsKey; splitId: number | string }>,
      ) => {
        const { trackId, splitId } = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (!trackState) return

        const deletedSplit = trackState.splits.find(
          s => s.id === splitId,
        ) as PublishingSplitData

        const publishingSplits = trackState.splits.filter(
          s => s.kind === 'publishing',
        ) as PublishingSplitData[]
        const recordingSplits = trackState.splits.filter(
          s => s.kind === 'recording',
        ) as RecordingSplitData[]

        if (
          publishingSplits.length === 1 &&
          publishingSplits[0]?.id === splitId
        ) {
          // Last remaining row: clear its data instead of removing it
          const emptySplit = createEmptySplit('publishing')
          trackState.splits = [emptySplit, ...recordingSplits]
        } else {
          trackState.splits = trackState.splits.filter(s => s.id !== splitId)
        }

        // Skip marking dirty if the deleted split was already an empty, unsaved placeholder
        // (e.g., user loaded a track with no writers and immediately clicks delete on the empty row)
        const wasEmptyPlaceholder =
          deletedSplit?.isNew === true &&
          deletedSplit.writerId === null &&
          deletedSplit.split == null
        if (!wasEmptyPlaceholder) {
          trackState.isDirty = true
        }

        // Remove validation errors for the deleted/cleared split
        if (trackState.validationErrors.length > 0) {
          trackState.validationErrors = trackState.validationErrors.filter(
            error => error.splitId !== splitId,
          )
        }
      },
    ),

    deleteRecordingSplit: create.reducer(
      (
        state,
        action: PayloadAction<{ trackId: SplitsKey; splitId: number | string }>,
      ) => {
        const { trackId, splitId } = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (!trackState) return

        const deletedSplit = trackState.splits.find(
          s => s.id === splitId,
        ) as RecordingSplitData

        const recordingSplits = trackState.splits.filter(
          s => s.kind === 'recording',
        ) as RecordingSplitData[]

        const publishingSplits = trackState.splits.filter(
          s => s.kind === 'publishing',
        ) as PublishingSplitData[]

        if (
          recordingSplits.length === 1 &&
          recordingSplits[0]?.id === splitId
        ) {
          // Last remaining row: clear its data instead of removing it
          const emptySplit = createEmptySplit('recording')
          trackState.splits = [...publishingSplits, emptySplit]
        } else {
          trackState.splits = trackState.splits.filter(s => s.id !== splitId)
        }

        const wasEmptyPlaceholder =
          deletedSplit?.isNew === true &&
          deletedSplit.rightsholderId === null &&
          (deletedSplit.split === 0 || deletedSplit.split === null)
        if (!wasEmptyPlaceholder) {
          trackState.isDirty = true
        }

        // Remove validation errors for the deleted/cleared split
        if (trackState.validationErrors.length > 0) {
          trackState.validationErrors = trackState.validationErrors.filter(
            error => error.splitId !== splitId,
          )
        }
      },
    ),

    /**
     * Loads existing rights data for a track from the server state.
     * Should be triggered when user switches to Writers tab.
     * Creates an empty split if no splits exist to avoid empty state.
     * @param trackId - The track ID to load data for
     * @param writerSplits - The existing writer splits from server state (track_writers)
     */
    loadTrackRightsData: create.reducer(
      (
        state,
        action: PayloadAction<{
          trackId: TrackId
          writerSplits: WriterSplit[]
        }>,
      ) => {
        const { trackId, writerSplits } = action.payload

        // If no splits exist, use initial state with empty split placeholder
        if (writerSplits.length === 0) {
          state.splitsByTrack[trackId] =
            createInitialTrackRightsStateWithEmptySplit()
          return
        }

        state.splitsByTrack[trackId] = {
          splits: writerSplits.map(convertWriterSplitToSplitData),
          isDirty: false,
          isLoading: false,
          error: null,
          validationErrors: [],
        }
      },
    ),

    loadTrackRightsFromRightsApi: create.reducer(
      (
        state,
        action: PayloadAction<{
          trackId: TrackId
          rightsSplitChains: RightsSplitChain[]
        }>,
      ) => {
        const { trackId, rightsSplitChains } = action.payload

        const splits = rightsSplitChains.map(convertRightsSplitChainToSplitData)
        const recordingSplits = splits.filter(
          s => s.kind === 'recording',
        ) as RecordingSplitData[]
        const publishingSplits = splits.filter(
          s => s.kind === 'publishing',
        ) as PublishingSplitData[]
        const finalSplits = [...publishingSplits, ...recordingSplits]

        if (publishingSplits.length === 0) {
          finalSplits.push(createEmptySplit('publishing'))
        }

        if (recordingSplits.length === 0) {
          finalSplits.push(createEmptySplit('recording'))
        }

        state.splitsByTrack[trackId] = {
          splits: finalSplits,
          isDirty: false,
          isLoading: false,
          error: null,
          validationErrors: [],
        }
      },
    ),

    /**
     * Initializes the bulk edit staging area with an empty split.
     * Used when editing multiple tracks at once.
     */
    initBulkEdit: create.reducer(state => {
      state.splitsByTrack[BULK_EDIT_KEY] =
        createInitialTrackRightsStateWithEmptySplit()
    }),

    /**
     * Clears the bulk edit staging area.
     */
    clearBulkEdit: create.reducer(state => {
      delete state.splitsByTrack[BULK_EDIT_KEY]
    }),

    /**
     * Clears all split data for a track, removing it from the store entirely.
     * @param trackId - The track ID or BULK_EDIT_KEY to clear
     */
    clearTrackChanges: create.reducer(
      (state, action: PayloadAction<SplitsKey>) => {
        delete state.splitsByTrack[action.payload]
      },
    ),

    /**
     * Resets the entire rights management state.
     */
    resetRightsManagement: create.reducer(() => initialState),

    /**
     * Sets loading state for a track.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param isLoading - Whether the track is loading
     */
    setTrackLoading: create.reducer(
      (
        state,
        action: PayloadAction<{ trackId: SplitsKey; isLoading: boolean }>,
      ) => {
        const { trackId, isLoading } = action.payload
        if (!state.splitsByTrack[trackId]) {
          state.splitsByTrack[trackId] = { ...initialTrackRightsState }
        }
        state.splitsByTrack[trackId].isLoading = isLoading
      },
    ),

    /**
     * Sets error state for a track.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param error - The error message or null to clear
     */
    setTrackError: create.reducer(
      (
        state,
        action: PayloadAction<{ trackId: SplitsKey; error: string | null }>,
      ) => {
        const { trackId, error } = action.payload
        if (!state.splitsByTrack[trackId]) {
          state.splitsByTrack[trackId] = { ...initialTrackRightsState }
        }
        state.splitsByTrack[trackId].error = error
        state.splitsByTrack[trackId].isLoading = false
      },
    ),

    /**
     * Sets field-level validation errors for a track's splits.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param validationErrors - Array of split validation errors
     */
    setTrackValidationErrors: create.reducer(
      (
        state,
        action: PayloadAction<{
          trackId: SplitsKey
          validationErrors: SplitValidationError[]
        }>,
      ) => {
        const { trackId, validationErrors } = action.payload
        if (!state.splitsByTrack[trackId]) {
          state.splitsByTrack[trackId] = { ...initialTrackRightsState }
        }
        state.splitsByTrack[trackId].validationErrors = validationErrors
        state.splitsByTrack[trackId].isLoading = false
      },
    ),

    /**
     * Clears all validation errors for a track.
     * @param trackId - The track ID or BULK_EDIT_KEY to clear errors for
     */
    clearTrackValidationErrors: create.reducer(
      (state, action: PayloadAction<SplitsKey>) => {
        const trackId = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (trackState) {
          trackState.validationErrors = []
        }
      },
    ),

    /**
     * Sets a field-level validation error for a specific split.
     * Used for client-side validation on blur.
     * These errors are marked as client-side and will persist until the user fixes them.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param splitId - The split ID to set the error for
     * @param field - The field name (e.g., 'writer', 'split_percentage')
     * @param message - The error message (empty string to clear the error)
     */
    setSplitFieldError: create.reducer(
      (
        state,
        action: PayloadAction<{
          trackId: SplitsKey
          splitId: number | string
          field: string
          message: string
        }>,
      ) => {
        const { trackId, splitId, field, message } = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (!trackState) return

        // Find existing validation error for this split
        let splitError = trackState.validationErrors.find(
          e => e.splitId === splitId,
        )

        if (message) {
          // Add or update the error
          if (!splitError) {
            // Create new split error
            splitError = { splitId, fieldErrors: [] }
            trackState.validationErrors.push(splitError)
          }

          // Find existing field error
          const fieldError = splitError.fieldErrors.find(
            fe => fe.field === field,
          )
          if (fieldError) {
            // Update existing field error
            fieldError.messages = [message]
            fieldError.isClientSide = true
          } else {
            // Add new field error with isClientSide flag
            splitError.fieldErrors.push({
              field,
              messages: [message],
              isClientSide: true,
            })
          }
        } else {
          // Clear the error for this field
          if (splitError) {
            splitError.fieldErrors = splitError.fieldErrors.filter(
              fe => fe.field !== field,
            )
            // Remove the split error if no more field errors remain
            if (splitError.fieldErrors.length === 0) {
              trackState.validationErrors = trackState.validationErrors.filter(
                e => e.splitId !== splitId,
              )
            }
          }
        }
      },
    ),

    /**
     * Clears all validation errors for a specific split.
     * Used when a row becomes an empty placeholder.
     * @param trackId - The track ID or BULK_EDIT_KEY
     * @param splitId - The split ID to clear errors for
     */
    clearSplitValidationErrors: create.reducer(
      (
        state,
        action: PayloadAction<{
          trackId: SplitsKey
          splitId: number | string
        }>,
      ) => {
        const { trackId, splitId } = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (!trackState) return

        trackState.validationErrors = trackState.validationErrors.filter(
          e => e.splitId !== splitId,
        )
      },
    ),

    /**
     * Marks a track as saved (not dirty) after successful save operation.
     * Also converts any new splits to existing by removing the isNew flag.
     * @param trackId - The track ID or BULK_EDIT_KEY that was saved
     */
    markTrackSaved: create.reducer(
      (state, action: PayloadAction<SplitsKey>) => {
        const trackId = action.payload
        const trackState = state.splitsByTrack[trackId]
        if (!trackState) return

        trackState.isDirty = false
        trackState.splits = trackState.splits.map(split => ({
          ...split,
          isNew: false,
        }))
      },
    ),

    /**
     * Saves writer splits for a track to the server.
     * Validates that all splits have valid writers and split percentages.
     * @param trackId - The track ID to save splits for
     */
    saveWriterSplits: create.asyncThunk<
      { trackId: TrackId; splits: SplitData[] },
      TrackId,
      {
        rejectValue: {
          trackId: TrackId
          error: string
          validationErrors?: SplitValidationError[]
          trackLevelErrors?: TrackLevelValidationError[]
        }
      }
    >(
      async (trackId, thunkApi) => {
        const state = thunkApi.getState() as State
        const trackState = state.rightsManagement?.splitsByTrack[trackId]

        if (!trackState) {
          return thunkApi.rejectWithValue({
            trackId,
            error: 'No track data found',
          })
        }

        // Filter out splits with empty writers (placeholders)
        // TODO: Include 'recording' splits in validSplits once we know what validation we need here too.
        const validSplits = trackState.splits
          .filter(s => s.kind === 'publishing')
          .filter(
            (split: PublishingSplitData) =>
              typeof split.writerId === 'number' && split.writerId > 0,
          )

        // Convert SplitData to API format
        // Note: API expects split_percentage as integer (25% stored as 2500)
        // When validSplits is empty, this sends { splits: [] } to clear all splits on the server
        const processWriterSplit = (split: PublishingSplitData) => ({
          writer: split.writerId as number,
          publisher:
            typeof split.publisherId === 'number' ? split.publisherId : null,
          split_percentage: Math.round((split.split ?? 0) * 100),
          pro: typeof split.proId === 'number' ? split.proId : null,
        })

        const processRecordingSplit = (split: RecordingSplitData) => ({
          rightsholder: split.rightsholderId as number,
          label: typeof split.labelId === 'number' ? split.labelId : null,
          split_percentage: Math.round((split.split ?? 0) * 100),
        })

        const requestData = {
          splits: validSplits.map(split => {
            if (split.kind === 'publishing') {
              return processWriterSplit(split as PublishingSplitData)
            } else {
              // TODO: Some processing for recording splits.
              return processRecordingSplit(split as RecordingSplitData)
            }
          }),
        }

        try {
          const response = await saveWriterSplitsApi(trackId, requestData)

          // Disabled as there is "Saved" notification is shown in the UI
          /*
          pushUINotification({
            message: 'Writer splits saved successfully',
            level: 'success',
          })
          */

          // Convert response to SplitData format
          const savedSplits = response.splits.map(
            convertResponseItemToSplitData,
          )
          return { trackId, splits: savedSplits }
        } catch (error: any) {
          const errorBody = error?.body || error

          // Check for track-level validation errors first (e.g., duplicate writer-publisher combinations)
          if (hasTrackLevelValidationErrors(errorBody)) {
            const parsed = parseSplitValidationErrors(errorBody, validSplits)

            // Display the actual track-level error messages instead of generic message
            const errorMessages = parsed.trackErrors?.map(e => e.message) || []
            const errorMessage =
              errorMessages.length > 0
                ? errorMessages.join('; ')
                : 'Please fix the validation errors'

            return thunkApi.rejectWithValue({
              trackId,
              error: errorMessage,
              trackLevelErrors: parsed.trackErrors,
            })
          }

          // Check if this is a split validation error with field-level errors
          if (hasSplitValidationErrors(errorBody)) {
            // Pass validSplits (not trackState.splits) to correctly map backend indices to split IDs.
            // The backend only receives validSplits, so error indices correspond to that array.
            const parsed = parseSplitValidationErrors(errorBody, validSplits)

            // Use generic error message for DiscoCallout display
            // Specific errors will be shown at the field level
            return thunkApi.rejectWithValue({
              trackId,
              error: 'Please fix the validation errors',
              validationErrors: parsed.splitErrors,
            })
          }

          // Fall back to generic error handling
          const errorMessage = parseServerError(errorBody)
          pushUINotification({
            message: errorMessage || 'Failed to save writer splits',
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            trackId,
            error: errorMessage || 'Failed to save writer splits',
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const trackId = meta.arg
          if (state.splitsByTrack[trackId]) {
            state.splitsByTrack[trackId].isLoading = true
            state.splitsByTrack[trackId].error = null
            // Clear previous validation errors when starting a new save
            state.splitsByTrack[trackId].validationErrors = []
            state.splitsByTrack[trackId].trackLevelErrors = undefined
          }
        },
        fulfilled: (state, action) => {
          const { trackId, splits } = action.payload
          if (state.splitsByTrack[trackId]) {
            state.splitsByTrack[trackId].isLoading = false
            state.splitsByTrack[trackId].isDirty = false
            state.splitsByTrack[trackId].error = null
            state.splitsByTrack[trackId].validationErrors = []
            state.splitsByTrack[trackId].trackLevelErrors = undefined
            // Update with server response data (includes real IDs)
            if (splits.length > 0) {
              state.splitsByTrack[trackId].splits = splits
            } else {
              // If no valid splits were saved, keep an empty placeholder
              state.splitsByTrack[trackId].splits = [
                createEmptySplit('publishing'),
              ]
            }
          }
        },
        rejected: (state, action) => {
          const { trackId, error, validationErrors, trackLevelErrors } =
            action.payload || {
              trackId: action.meta.arg,
              error: 'Unknown error',
            }
          if (state.splitsByTrack[trackId]) {
            state.splitsByTrack[trackId].isLoading = false
            state.splitsByTrack[trackId].error = error || null
            // Store field-level validation errors if present
            if (validationErrors && validationErrors.length > 0) {
              state.splitsByTrack[trackId].validationErrors = validationErrors
            }
            // Store track-level validation errors if present
            if (trackLevelErrors && trackLevelErrors.length > 0) {
              state.splitsByTrack[trackId].trackLevelErrors = trackLevelErrors
            }
          }
        },
      },
    ),

    /**
     * Clears the bulk save results.
     * Should be called when starting a new bulk operation or closing the dialog.
     */
    clearBulkSaveResults: create.reducer(state => {
      state.bulkSave = initialBulkSaveState
    }),

    /**
     * Stores per-track failures from a bulk save error so the review modal can
     * render them.
     */
    setBulkSaveFailures: create.reducer<BulkSaveTrackResult[]>(
      (state, action) => {
        state.bulkSave.results = action.payload
        state.bulkSave.failureCount = action.payload.length
      },
    ),

    /**
     * Saves writer splits for multiple tracks in append mode.
     * Validates that all splits have valid writers and split percentages.
     * @param trackIds - Array of track IDs to save splits to
     * @returns Results for each track with success/failure status
     */
    batchSaveWriterSplits: create.asyncThunk<
      {
        results: BulkSaveTrackResult[]
        successCount: number
        failureCount: number
      },
      { trackIds: TrackId[]; overwrite?: boolean },
      {
        rejectValue: {
          error: string
          results?: BulkSaveTrackResult[]
        }
      }
    >(
      async ({ trackIds, overwrite = false }, thunkApi) => {
        const state = thunkApi.getState() as State
        const bulkEditState =
          state.rightsManagement?.splitsByTrack[BULK_EDIT_KEY]

        if (!bulkEditState) {
          return thunkApi.rejectWithValue({
            error: 'No bulk edit data found',
          })
        }

        // Filter to only audio tracks, as writer splits only apply to audio
        // If tracks reducer is not available (e.g., in tests), treat all tracks as audio
        const audioTrackIds = state.tracks
          ? trackIds.filter(trackId => {
              const track = state.tracks?.[trackId]
              if (!track) return false
              const fileType = getFileTypeByExt(track.ext)
              return fileType === AUDIO_FILE
            })
          : (() => {
              // Warn in non-test environments when tracks reducer is unavailable
              if (process.env.NODE_ENV !== 'test') {
                console.warn(
                  'Tracks reducer unavailable - treating all trackIds as audio tracks. ' +
                    'This may cause issues if non-audio tracks are included.',
                )
              }
              return trackIds
            })()

        if (audioTrackIds.length === 0) {
          return thunkApi.rejectWithValue({
            error:
              'No audio tracks selected. Writer splits only apply to audio tracks.',
          })
        }

        // Filter out splits with empty writers (placeholders)
        const splits: PublishingSplitData[] = bulkEditState.splits.filter(
          s => s.kind === 'publishing',
        ) as PublishingSplitData[]
        const validSplits = splits.filter(
          split => typeof split.writerId === 'number' && split.writerId > 0,
        )

        if (validSplits.length === 0) {
          return thunkApi.rejectWithValue({
            error: 'No valid splits to save. Please add at least one writer.',
          })
        }

        // Convert SplitData to API format
        // Note: API expects split_percentage as integer (25% stored as 2500, same format as single-track API)
        const splitsPayload = validSplits.map(split => ({
          writer: split.writerId as number,
          publisher:
            typeof split.publisherId === 'number' ? split.publisherId : null,
          split_percentage: Math.round((split.split ?? 0) * 100),
          pro: typeof split.proId === 'number' ? split.proId : null,
        }))

        try {
          const response = await batchSaveWriterSplitsApi({
            track_ids: audioTrackIds,
            splits: splitsPayload,
            overwrite,
          })

          // Handle success response
          if (isBatchWriterSplitsSuccessResponse(response)) {
            const results: BulkSaveTrackResult[] = response.tracks.map(
              track => ({
                trackId: track.track_id,
                success: true,
              }),
            )

            return {
              results,
              successCount: response.tracks.length,
              failureCount: 0,
            }
          }

          // Handle error response (validation failures like splits exceeding 100%)
          if (isBatchWriterSplitsErrorResponse(response)) {
            const errorsByTrackId = groupErrorsByTrackId(response.splits)

            // Convert grouped errors to results
            const results: BulkSaveTrackResult[] = Array.from(
              errorsByTrackId.entries(),
            ).map(([trackId, errors]) => ({
              trackId,
              success: false,
              errors,
            }))

            return {
              results,
              successCount: 0,
              failureCount: results.length,
            }
          }

          // Fallback for unexpected response format
          return thunkApi.rejectWithValue({
            error: 'Unexpected response format from server',
          })
        } catch (error: unknown) {
          // Log for debugging purposes
          console.error('Batch save failed:', error)
          // Extract error body from API response, or use the error directly
          const apiError = error as { body?: unknown } | undefined
          const errorBody = apiError?.body ?? error

          // Check if the error body is a validation error response (400 status with splits array)
          // The backend returns 400 errors with the same structure as the success response
          if (
            errorBody &&
            typeof errorBody === 'object' &&
            'splits' in errorBody &&
            Array.isArray(errorBody.splits)
          ) {
            const errorsByTrackId = groupErrorsByTrackId(errorBody.splits)

            // Convert grouped errors to results
            const results: BulkSaveTrackResult[] = Array.from(
              errorsByTrackId.entries(),
            ).map(([trackId, errors]) => ({
              trackId,
              success: false,
              errors,
            }))

            return thunkApi.rejectWithValue({
              error: 'Validation failed for some tracks',
              results,
            })
          }

          // Fall back to generic error parsing for other error types
          const errorMessage = parseServerError(
            errorBody as Parameters<typeof parseServerError>[0],
          )
          return thunkApi.rejectWithValue({
            error: errorMessage || 'Failed to save writer splits',
          })
        }
      },
      {
        pending: state => {
          state.bulkSave.isLoading = true
          state.bulkSave.error = null
          state.bulkSave.results = []
          state.bulkSave.successCount = 0
          state.bulkSave.failureCount = 0
        },
        fulfilled: (state, action) => {
          const { results, successCount, failureCount } = action.payload
          state.bulkSave.isLoading = false
          state.bulkSave.results = results
          state.bulkSave.successCount = successCount
          state.bulkSave.failureCount = failureCount
          state.bulkSave.error = null

          // Only clear the bulk edit staging area on full success.
          // On partial failures, keep the data so users can retry.
          // The modal component is responsible for calling clearBulkEdit() on close.
          if (successCount > 0 && failureCount === 0) {
            delete state.splitsByTrack[BULK_EDIT_KEY]
          }
        },
        rejected: (state, action) => {
          const { error, results } = action.payload || {
            error: 'Unknown error',
          }
          state.bulkSave.isLoading = false
          state.bulkSave.error = error
          if (results) {
            state.bulkSave.results = results
          }
        },
      },
    ),
  }),
})

// Export actions
export const {
  addSplit,
  updateSplit,
  deletePublishingSplit,
  deleteRecordingSplit,
  loadTrackRightsData,
  loadTrackRightsFromRightsApi,
  initBulkEdit,
  clearBulkEdit,
  clearTrackChanges,
  resetRightsManagement,
  setTrackLoading,
  setTrackError,
  setTrackValidationErrors,
  clearTrackValidationErrors,
  setSplitFieldError,
  clearSplitValidationErrors,
  markTrackSaved,
  saveWriterSplits,
  clearBulkSaveResults,
  setBulkSaveFailures,
  batchSaveWriterSplits,
} = rightsManagementSlice.actions

// Selectors

/**
 * Gets the rights management state for a specific track or bulk edit.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns The track rights state or undefined
 */
export const getTrackRightsState = (
  state: State,
  trackId: SplitsKey,
): TrackRightsState | undefined =>
  state.rightsManagement?.splitsByTrack[trackId]

/**
 * Gets splits for a specific track or bulk edit.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns Array of splits or empty array
 */
export const getTrackSplits = (state: State, trackId: SplitsKey): SplitData[] =>
  state.rightsManagement?.splitsByTrack[trackId]?.splits ?? []

export const getTrackPublishingSplits = (
  state: State,
  trackId: SplitsKey,
): PublishingSplitData[] => {
  const splits =
    (state.rightsManagement?.splitsByTrack[trackId]?.splits.filter(
      s => s.kind === 'publishing',
    ) as unknown) ?? []
  return splits as PublishingSplitData[]
}

export const getTrackRecordingSplits = (
  state: State,
  trackId: SplitsKey,
): RecordingSplitData[] => {
  const splits =
    (state.rightsManagement?.splitsByTrack[trackId]?.splits.filter(
      s => s.kind === 'recording',
    ) as unknown) ?? []
  return splits as RecordingSplitData[]
}

/**
 * Gets whether a track or bulk edit has unsaved changes.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns True if track has unsaved changes
 */
export const isTrackDirty = (state: State, trackId: SplitsKey): boolean =>
  state.rightsManagement?.splitsByTrack[trackId]?.isDirty ?? false

/**
 * Gets whether a track or bulk edit is loading.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns True if track is loading
 */
export const isTrackLoading = (state: State, trackId: SplitsKey): boolean =>
  state.rightsManagement?.splitsByTrack[trackId]?.isLoading ?? false

/**
 * Gets the error for a track or bulk edit.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns Error message or null
 */
export const getTrackError = (
  state: State,
  trackId: SplitsKey,
): string | null =>
  state.rightsManagement?.splitsByTrack[trackId]?.error ?? null

/**
 * Gets the field-level validation errors for a track or bulk edit.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns Array of split validation errors or empty array
 */
export const getTrackValidationErrors = (
  state: State,
  trackId: SplitsKey,
): SplitValidationError[] =>
  state.rightsManagement?.splitsByTrack[trackId]?.validationErrors ?? []

/**
 * Checks if a track has any field-level validation errors.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns True if there are validation errors
 */
export const hasTrackValidationErrors = (
  state: State,
  trackId: SplitsKey,
): boolean => {
  const errors =
    state.rightsManagement?.splitsByTrack[trackId]?.validationErrors
  return errors !== undefined && errors.length > 0
}

/**
 * Gets track-level validation errors for a track.
 * Track-level errors apply to the entire track rather than specific fields,
 * such as duplicate writer-publisher combinations.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns Array of track-level validation errors or empty array
 */
export const getTrackLevelErrors = (
  state: State,
  trackId: SplitsKey,
): TrackLevelValidationError[] =>
  state.rightsManagement?.splitsByTrack[trackId]?.trackLevelErrors ?? []

/**
 * Checks if a track has any track-level validation errors.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns True if there are track-level validation errors
 */
export const hasTrackLevelErrors = (
  state: State,
  trackId: SplitsKey,
): boolean => {
  const errors =
    state.rightsManagement?.splitsByTrack[trackId]?.trackLevelErrors
  return errors !== undefined && errors.length > 0
}

/**
 * Gets all track IDs that have been loaded into rights management.
 * @param state - The Redux state
 * @returns Array of track IDs
 */
export const getLoadedTrackIds = (state: State): TrackId[] =>
  Object.keys(state.rightsManagement?.splitsByTrack ?? {}).map(Number)

/**
 * Gets all track IDs that have unsaved changes.
 * @param state - The Redux state
 * @returns Array of track IDs with unsaved changes
 */
export const getDirtyTrackIds = createSelector(
  [(state: State) => state.rightsManagement?.splitsByTrack ?? {}],
  (splitsByTrack): TrackId[] =>
    Object.entries(splitsByTrack)
      .filter(
        (entry): entry is [string, TrackRightsState] =>
          entry[1] !== undefined && entry[1].isDirty,
      )
      .filter(([trackId]) => trackId !== BULK_EDIT_KEY)
      .map(([trackId]) => Number(trackId)),
)

/**
 * Calculates the total split percentage for a track or bulk edit.
 * Uses toFixed to prevent precision errors when summing decimals.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns Total percentage (should equal 100 for valid splits)
 */
const _getSplitPercentage = (splits: SplitData[]): number => {
  const total = splits.reduce((total, split) => total + (split.split || 0), 0)
  return parseFloat(total.toFixed(2))
}

export const getTotalPublishingSplitPercentage = (
  state: State,
  trackId: SplitsKey,
): number => {
  const splits = getTrackPublishingSplits(state, trackId)
  return _getSplitPercentage(splits)
}

export const getTotalRecordingSplitPercentage = (
  state: State,
  trackId: SplitsKey,
): number => {
  const splits = getTrackRecordingSplits(state, trackId)
  return _getSplitPercentage(splits)
}

/**
 * Validation result for writer splits.
 */
export type PublishingSplitsValidation = {
  isValid: boolean
  errors: {
    splitsTotalAbove100: boolean
    splitsTotalNotEqual100: boolean
    hasEmptyWriters: boolean
  }
}

export type RecordingSplitsValidation = {
  isValid: boolean
  errors: {
    recSplitsTotalAbove100: boolean
    recSplitsTotalNotEqual100: boolean
    missingRightsholderAndLabel: boolean
  }
}

export type SplitsValidation = PublishingSplitsValidation &
  RecordingSplitsValidation

export const validatePublishingSplits = (
  state: State,
  trackId: SplitsKey,
  splits: PublishingSplitData[],
) => {
  // Filter to splits that have a writer selected (not placeholders)
  const splitsWithWriters = splits.filter(split =>
    hasSplitFieldValue(split.writerId),
  )

  // Check for splits that have data but are missing a writer
  // A split needs a writer when it has any non-default value: a split percentage (including 0),
  const hasEmptyWriters = splits.some(split => {
    const writerMissing = !hasSplitFieldValue(split.writerId)
    if (!writerMissing) return false
    const hasPublisher = hasSplitFieldValue(split.publisherId)
    const hasAdmin = hasSplitFieldValue(split.adminId)
    const hasPro = hasSplitFieldValue(split.proId)
    return split.split != null || hasPublisher || hasAdmin || hasPro
  })

  // Calculate total for ALL splits (including those without writers)
  // This is used for the warning callout to show when total exceeds 100%
  const allSplitsTotal = splits.reduce(
    (sum, split) => sum + (split.split || 0),
    0,
  )
  const fixedAllSplitsTotal = parseFloat(allSplitsTotal.toFixed(2))
  const splitsTotalAbove100 = fixedAllSplitsTotal > 100

  // If there are no valid splits with writers, check if total > 100%
  // (empty state is allowed - user can save with no writers, but warn if total > 100%)
  if (splitsWithWriters.length === 0) {
    return {
      isValid: !hasEmptyWriters && !splitsTotalAbove100,
      errors: {
        splitsTotalAbove100,
        splitsTotalNotEqual100: false,
        hasEmptyWriters,
      },
    }
  }

  // Calculate total for splits with valid writers (for the != 100% warning)
  // Use toFixed to prevent precision errors
  const writersTotal = splitsWithWriters.reduce(
    (sum, split) => sum + (split.split || 0),
    0,
  )
  const fixedWritersTotal = parseFloat(writersTotal.toFixed(2))
  const splitsTotalNotEqual100 = fixedWritersTotal !== 100

  return {
    isValid: !splitsTotalAbove100 && !hasEmptyWriters,
    errors: {
      splitsTotalAbove100,
      splitsTotalNotEqual100,
      hasEmptyWriters,
    },
  }
}

export const validateRecordingSplits = (
  state: State,
  trackId: SplitsKey,
  splits: RecordingSplitData[],
): RecordingSplitsValidation => {
  const nonPlaceholderSplits = splits.filter(s => {
    return (
      hasSplitFieldValue(s.rightsholderId) ||
      hasSplitFieldValue(s.labelId) ||
      hasSplitFieldValue(s.split)
    )
  })

  const allSplitsTotal = nonPlaceholderSplits.reduce(
    (sum, split) => sum + (split.split || 0),
    0,
  )
  const fixedAllSplitsTotal = parseFloat(allSplitsTotal.toFixed(2))
  const splitsTotalAbove100 = fixedAllSplitsTotal > 100
  const splitsTotalNotEqual100 = fixedAllSplitsTotal !== 100
  let missingRightsholderAndLabel = false

  if (nonPlaceholderSplits.length > 0) {
    missingRightsholderAndLabel = nonPlaceholderSplits.some(split => {
      return (
        !hasSplitFieldValue(split.rightsholderId) &&
        !hasSplitFieldValue(split.labelId)
      )
    })
  }

  return {
    isValid: !splitsTotalAbove100 && !missingRightsholderAndLabel,
    errors: {
      recSplitsTotalAbove100: splitsTotalAbove100,
      missingRightsholderAndLabel,
      recSplitsTotalNotEqual100: splitsTotalNotEqual100,
    },
  }
}

/**
 * Gets validation state for writer splits.
 * Checks that:
 * - All splits with non-zero percentage have a writer selected
 * - All splits with a writer have a valid split percentage (>= 0)
 * - Total of ALL splits (including those without writers) does not exceed 100%
 * - If there are valid splits with writers, total must equal 100%
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns Validation result
 */
export const getSplitsValidation = (
  state: State,
  trackId: SplitsKey,
  recordingTabEnabled: boolean = true,
): SplitsValidation => {
  const splits = getTrackSplits(state, trackId)
  const publishingSplits = splits.filter(split => split.kind === 'publishing')
  const recordingSplits = splits.filter(split => split.kind === 'recording')

  let publishingValidationObj = {
    isValid: true,
    errors: {
      splitsTotalAbove100: false,
      splitsTotalNotEqual100: false,
      hasEmptyWriters: false,
    },
  }
  let recordingValidationObj: RecordingSplitsValidation = {
    isValid: true,
    errors: {
      recSplitsTotalAbove100: false,
      recSplitsTotalNotEqual100: false,
      missingRightsholderAndLabel: false,
    },
  }

  if (publishingSplits.length > 0) {
    publishingValidationObj = validatePublishingSplits(
      state,
      trackId,
      publishingSplits as PublishingSplitData[],
    )
  }

  if (recordingTabEnabled && recordingSplits.length > 0) {
    recordingValidationObj = validateRecordingSplits(
      state,
      trackId,
      recordingSplits as RecordingSplitData[],
    )
  }

  return {
    isValid: publishingValidationObj.isValid && recordingValidationObj.isValid,
    errors: {
      ...publishingValidationObj.errors,
      ...recordingValidationObj.errors,
    },
  }
}

/**
 * Returns true if the splits are valid and can be saved.
 * @param state - The Redux state
 * @param trackId - The track ID or BULK_EDIT_KEY
 * @returns True if splits are valid for saving
 */
export const canSaveSplits = (state: State, trackId: SplitsKey): boolean => {
  const validation = getSplitsValidation(state, trackId)
  return validation.isValid
}

// Bulk save selectors

/**
 * Gets the bulk save state.
 * @param state - The Redux state
 * @returns The bulk save state
 */
export const getBulkSaveState = (state: State): BulkSaveState =>
  state.rightsManagement?.bulkSave ?? initialBulkSaveState

/**
 * Gets whether a bulk save operation is in progress.
 * @param state - The Redux state
 * @returns True if bulk save is loading
 */
export const isBulkSaveLoading = (state: State): boolean =>
  state.rightsManagement?.bulkSave?.isLoading ?? false

/**
 * Gets the bulk save results.
 * @param state - The Redux state
 * @returns Array of bulk save results per track
 */
export const getBulkSaveResults = (state: State): BulkSaveTrackResult[] =>
  state.rightsManagement?.bulkSave?.results ?? []

/**
 * Gets the bulk save success count.
 * @param state - The Redux state
 * @returns Number of successfully updated tracks
 */
export const getBulkSaveSuccessCount = (state: State): number =>
  state.rightsManagement?.bulkSave?.successCount ?? 0

/**
 * Gets the bulk save failure count.
 * @param state - The Redux state
 * @returns Number of failed tracks
 */
export const getBulkSaveFailureCount = (state: State): number =>
  state.rightsManagement?.bulkSave?.failureCount ?? 0

/**
 * Gets the bulk save error message.
 * @param state - The Redux state
 * @returns Error message or null
 */
export const getBulkSaveError = (state: State): string | null =>
  state.rightsManagement?.bulkSave?.error ?? null

/**
 * Gets only the failed track results from bulk save.
 * @param state - The Redux state
 * @returns Array of failed track results
 */
export const getFailedBulkSaveResults = (state: State): BulkSaveTrackResult[] =>
  (state.rightsManagement?.bulkSave?.results ?? []).filter(
    result => !result.success,
  )

// Export reducer
export const rightsManagementReducer = rightsManagementSlice.reducer
